"use client";

import React, { useState } from "react";
import { motion, MotionConfig } from "framer-motion";
import { Instagram, Facebook, Linkedin, Globe, Plus } from "lucide-react";
import TextReveal from "./animations/TextReveal";

/* ───────────────────────── DATA (same content) ───────────────────────── */
const subunitsData = [
  {
    id: 1,
    name: "CS",
    fullName: "Computer Society",
    logo: "/asset/logos/cswh.png",
    bannerImage: "/asset/banners/csg.webp",
    description:
      "The IEEE Computer Society (CS) students branch chapter at isims is focused on advancing the fields of computing and technology. It provides students with opportunities to participate in workshops, coding competitions, and technical talks aimed at enhancing their skills in computer science and software engineering. The chapter also encourages collaboration and learning through various projects.",
    social: { instagram: "https://www.instagram.com/ieee.isims.cs/", facebook: "https://www.facebook.com/profile.php?id=100088090993586", linkedin: "https://www.linkedin.com/company/ieee-computer-society-chapter-isims-student-branch/posts/?feedView=all", website: "https://cs-isims.ieee.tn/" },
  },
  {
    id: 2,
    name: "IAS",
    fullName: "Industrial Applications Society",
    logo: "/asset/logos/iasjdid.png",
    bannerImage: "/asset/banners/iasg.webp",
    description:
      "The IEEE IAS students branch chapter at isims bridges the gap between students and the industry, focusing on applying engineering and scientific principles to solve industrial problems. Members have access to industry leaders and projects that provide real-world experience in power systems, automation, and industrial electronics.",
    social: { instagram: "https://www.instagram.com/ieee_ias_isims/", facebook: "https://www.facebook.com/profile.php?id=61572324778458", linkedin: "https://www.linkedin.com/company/ieee-ias-isims-sbc/posts/?feedView=all", website: "https://ias-isims.ieee.tn/" },
  },
  {
    id: 3,
    name: "IES-PES",
    fullName: "Power & Energy Society",
    logo: "/asset/logos/iespeswh.png",
    bannerImage: "/asset/banners/iesg.webp",
    description:
      "The IEEE Industrial Electronics Society (IES) and Power and Energy Society (PES) students branch chapter at isims focuses on the application of electronics in industrial processes and the generation, transmission, and distribution of electrical energy. The chapter provides students with insights into power systems, renewable energy technologies, and industrial automation through workshops, seminars, and collaborative projects.",
    social: { instagram: "https://www.instagram.com/ieee.ies.pes.isims.sb.jc/", facebook: "https://www.facebook.com/profile.php?id=61580047484275", linkedin: "https://www.linkedin.com/company/ieee-ies-pes-student-branch-chapter-%E2%80%93-isims/posts/?feedView=all", website: "https://ies-pes-isims.ieee.tn/" },
  },
  {
    id: 4,
    name: "WIE",
    fullName: "Women in Engineering",
    logo: "/asset/logos/wiejdid.png",
    bannerImage: "/asset/banners/wieg.webp",
    description:
      "The Women in Engineering (WIE) affinity group at isims focuses on promoting gender diversity in engineering and technology. It provides a platform for female engineers to connect, collaborate, and contribute to the advancement of engineering fields. The group organizes workshops, mentoring programs, and events that encourage leadership and innovation among women in STEM.",
    social: { instagram: "https://www.instagram.com/ieee_wie_isims_sag/", facebook: "https://www.facebook.com/profile.php?id=100088426925351", linkedin: "https://www.linkedin.com/in/ieee-wie-isims-student-affinity-group-2a7a4035a/", website: "https://wie-isims.ieee.tn/" },
  },
  {
    id: 5,
    name: "SIGHT",
    fullName: "Humanitarian Technology",
    logo: "/asset/logos/sightwh.png",
    bannerImage: "/asset/banners/sightg.webp",
    description:
      "The IEEE SIGHT isims Student Branch Group was founded in October 2025, this SBG aims to promote IEEE Humanitarian Technologies Activities and SIGHT goals within IEEE isims SB Community. Activities are mainly focused on sustainable SDG oriented technical projects.",
    social: { instagram: "https://www.instagram.com/ieee.isims.sight.group/", facebook: "https://www.facebook.com/profile.php?id=61584359810922", linkedin: "#", website: "#" },
  },
  {
    id: 6,
    name: "SMC",
    fullName: "Systems, Man & Cybernetics",
    logo: "/asset/logos/smcwh.png",
    bannerImage: "/asset/banners/smcg.webp",
    description:
      "The IEEE Systems, Man, and Cybernetics Society (SMC) students branch chapter at isims focuses on the interdisciplinary study of systems engineering, human-machine interaction, and intelligent systems. It provides students with opportunities to engage in activities such as technical workshops, innovation challenges, and research-oriented seminars covering topics like AI, cybernetics, and human-centered design.",
    social: { instagram: "https://www.instagram.com/ieee_smc_isims_sb_chapter/", facebook: "https://www.facebook.com/profile.php?id=61557150934003", linkedin: "https://www.linkedin.com/search/results/all/?keywords=smc%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://smc-isims.ieee.tn/" },
  },
  {
    id: 7,
    name: "RAS",
    fullName: "Robotics & Automation",
    logo: "/asset/logos/raswh.png",
    bannerImage: "/asset/banners/rasg.webp",
    description:
      "The Robotics and Automation Society (RAS) students branch chapter at isims is a hub for students interested in the fields of robotics, automation, and artificial intelligence. The chapter organizes hands-on sessions where members can build and program robots, and participate in national and international robotics competitions.",
    social: { instagram: "https://www.instagram.com/ieee_ras_chapter_isims_sb/", facebook: "https://www.facebook.com/profile.php?id=100090530634047", linkedin: "linkedin.com/search/results/all/?keywords=ieee%20ras%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://ras-isims.ieee.tn/" },
  },
];

const EASE = "ease-[cubic-bezier(0.16,1,0.3,1)]";
const pad = (n) => String(n).padStart(2, "0");

function SocialIcon({ href, icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/25 hover:bg-white hover:text-[#00334d] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7dd3fc]"
    >
      {React.cloneElement(icon, { className: "w-4 h-4" })}
    </a>
  );
}

/* One panel. Desktop: row of panels, the open one grows. Phone: stacked, the open one grows downward. */
function Panel({ unit, index, on, onOpen }) {
  const { social } = unit;
  const links = [
    ["instagram", "Instagram", <Instagram />],
    ["facebook", "Facebook", <Facebook />],
    ["linkedin", "LinkedIn", <Linkedin />],
    ["website", "Website", <Globe />],
  ].filter(([k]) => social[k] && social[k] !== "#");

  return (
    <article
      onMouseEnter={onOpen}
      style={{ flexGrow: on ? 8 : 1 }}
      className={`relative overflow-hidden bg-[#00334d] min-w-0 lg:basis-0 lg:h-full transition-[height,flex-grow] duration-[800ms] ${EASE} ${
        on ? "h-[600px] sm:h-[520px]" : "h-[72px]"
      }`}
    >
      <img
        src={unit.bannerImage}
        alt=""
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1200ms] ${EASE} ${
          on ? "opacity-70 scale-100" : "opacity-25 scale-110 grayscale"
        }`}
      />
      <div
        className="absolute inset-0 opacity-[0.1] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#7cc4e8 1px, transparent 1px), linear-gradient(90deg, #7cc4e8 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className={`absolute inset-0 bg-gradient-to-t from-[#00334d] via-[#00334d]/70 to-[#00334d]/10 transition-opacity duration-700 ${
          on ? "opacity-100" : "opacity-60"
        }`}
      />

      <span className={`absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#7dd3fc] z-10 pointer-events-none transition-opacity duration-500 ${on ? "opacity-100 delay-500" : "opacity-0"}`} />
      <span className={`absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#7dd3fc] z-10 pointer-events-none transition-opacity duration-500 ${on ? "opacity-100 delay-500" : "opacity-0"}`} />

      {/* closed state */}
      <div
        className={`absolute inset-0 z-[5] flex lg:flex-col items-center gap-4 lg:gap-0 lg:justify-between px-5 lg:px-0 lg:py-6 pointer-events-none transition-opacity duration-300 ${
          on ? "opacity-0" : "opacity-100 delay-300"
        }`}
        aria-hidden="true"
      >
        <span className="text-sm font-black text-[#7dd3fc] tabular-nums w-7 lg:w-auto">{pad(index + 1)}</span>
        <span
          className="text-3xl lg:text-4xl font-black leading-none tracking-tighter text-transparent uppercase lg:[writing-mode:vertical-rl] lg:rotate-180 lg:flex-1 lg:flex lg:items-center"
          style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.85)" }}
        >
          {unit.name}
        </span>
        <span className="ml-auto lg:ml-0 w-9 h-9 flex items-center justify-center border border-white/30 text-white">
          <Plus className="w-4 h-4" />
        </span>
      </div>

      {!on && (
        <button
          onClick={onOpen}
          onFocus={onOpen}
          aria-label={`Open ${unit.name}: ${unit.fullName}`}
          className="absolute inset-0 z-20 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7dd3fc]"
        />
      )}

      {/* open state, fixed inner width on desktop so text never reflows while growing */}
      <div
        className={`absolute inset-0 z-10 transition-[opacity,visibility] duration-500 ${
          on ? "visible opacity-100 delay-[350ms]" : "invisible opacity-0"
        }`}
      >
        <div className="relative h-full w-full lg:w-[min(780px,56vw)] flex flex-col justify-between p-6 sm:p-7 lg:p-9">
          <div className="flex items-start justify-between">
            <img
              src={unit.logo}
              alt={`${unit.name} logo`}
              className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 object-contain brightness-0 invert drop-shadow-2xl"
            />
            <span
              className="text-5xl sm:text-6xl lg:text-7xl font-black leading-none tracking-tighter text-transparent tabular-nums select-none"
              style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.7)" }}
              aria-hidden="true"
            >
              {pad(index + 1)}
            </span>
          </div>

          <div>
            <h3 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase leading-[0.95] tracking-tighter">
              {unit.name}
            </h3>
            <p className="mt-2 text-sm sm:text-base font-bold text-[#7dd3fc]">{unit.fullName}</p>
            <p className="mt-3 text-sm text-white/80 leading-relaxed max-w-[68ch]">{unit.description}</p>
            <div className="mt-5 flex items-center gap-2">
              {links.map(([k, label, icon]) => (
                <SocialIcon key={k} href={social[k]} icon={icon} label={`${unit.name} ${label}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Subunits() {
  const [activeId, setActiveId] = useState(subunitsData[0].id);

  return (
    <MotionConfig reducedMotion="user">
      <div id="subunits" className="relative border-t bg-white border-[#006699]/10 w-full py-12 md:py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              repeating-linear-gradient(-45deg, rgba(15,105,157,0.05) 0px, rgba(15,105,157,0.05) 1px, transparent 1px, transparent 18px),
              repeating-linear-gradient(45deg, rgba(15,105,157,0.04) 0px, rgba(15,105,157,0.04) 1px, transparent 1px, transparent 28px)
            `,
          }}
        />

        {/* L corners */}
        <div className="absolute top-4 left-4 sm:top-8 sm:left-8 w-12 h-12 sm:w-24 sm:h-24 border-l-2 border-t-2 border-[#006699]/20 pointer-events-none" />
        <div className="absolute top-4 right-4 sm:top-8 sm:right-8 w-12 h-12 sm:w-24 sm:h-24 border-r-2 border-t-2 border-[#006699]/20 pointer-events-none" />
        <div className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 w-12 h-12 sm:w-24 sm:h-24 border-l-2 border-b-2 border-[#006699]/20 pointer-events-none" />
        <div className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-24 sm:h-24 border-r-2 border-b-2 border-[#006699]/20 pointer-events-none" />

        <div className="relative max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-10 text-center">
            <div className="inline-block">
              <TextReveal as="h2" className="text-3xl sm:text-4xl md:text-5xl font-medium text-slate-800 tracking-tight">
                Discover Our Subunits
              </TextReveal>
              <div className="w-20 h-1 mt-3 bg-[#006699] mx-auto opacity-80" />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col lg:flex-row lg:h-[520px] gap-px bg-[#006699]/25 p-px shadow-xl"
          >
            {subunitsData.map((unit, i) => (
              <Panel
                key={unit.id}
                unit={unit}
                index={i}
                on={unit.id === activeId}
                onOpen={() => setActiveId(unit.id)}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </MotionConfig>
  );
}